import type { ReactNode } from "react";
import { useArchViz } from "./ArchVizContext";
import { VizHeaderControls } from "./VizHeaderControls";

interface TopBarProps {
  topBarSlot?: ReactNode;
  readOnly?: boolean;
  /** When false, suppresses the built-in ThemeToggle (use when an outer header already shows one). */
  showThemeToggle?: boolean;
}

/**
 * The single header row for the local upload / drop-zone route, which has no
 * outer page header. Shows the architecture name, then the shared breadcrumb +
 * search + constraints cluster. In the hosted solution views an outer page
 * header renders {@link VizHeaderControls} directly, so this bar is suppressed
 * (see ArchVizApp) to keep everything on one row.
 */
export function TopBar({
  topBarSlot,
  readOnly,
  showThemeToggle: _showThemeToggle = true,
}: TopBarProps = {}) {
  const { state } = useArchViz();
  const arch = state.architecture;

  if (!arch) return null;

  return (
    <div className="flex items-center px-4 py-2.5 border-b bg-card/80 backdrop-blur gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      {!readOnly && (
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <h1 className="text-lg truncate tracking-tight max-w-[220px]">{arch.meta.name}</h1>
          {arch.meta.version && (
            <span className="text-xs text-muted-foreground font-mono">v{arch.meta.version}</span>
          )}
          <span className="h-4 w-px bg-border max-md:hidden" aria-hidden="true" />
        </div>
      )}
      <VizHeaderControls />
      {topBarSlot && <div className="flex items-center shrink-0">{topBarSlot}</div>}
    </div>
  );
}
